import React from 'react';
import {MatchInsights} from './types';

interface MatchInsightsPanelProps {
    isLoading: boolean;
    matchInsights: MatchInsights | null;
    onRerun: () => void;
}

// Match-insights advisory panel (bd bqw.9). Renders only when the user has
// clicked "Get insights" on an existing blueprint. Extracted from
// PromptManager.tsx (deepcontext-ityf decomposition); restyled in
// deepcontext-pnxk as a canvas-inset panel with an accent narrative header,
// theme bullets and a footer stat line — behavior unchanged.
const MatchInsightsPanel: React.FC<MatchInsightsPanelProps> = ({isLoading, matchInsights, onRerun}) => (
    <div
        data-testid="match-insights-panel"
        className="mt-3.5 rounded-xl border border-line bg-canvas px-3.5 py-3"
    >
        <div className="flex items-center gap-1.5">
            <span aria-hidden className="text-sm text-accent-active">✦</span>
            <h4 className="text-sm font-extrabold text-accent-active">
                Blueprint match insights
            </h4>
            {matchInsights && !isLoading && (
                <button
                    type="button"
                    onClick={onRerun}
                    className="ml-auto text-xs font-bold text-accent-active underline hover:no-underline"
                >
                    Re-run
                </button>
            )}
        </div>

        {isLoading && (
            <div className="flex items-center gap-2 py-3 text-sm text-ink-muted">
                <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-accent"></span>
                <span>Sampling rejected URLs and asking the model… this may take ~10 seconds.</span>
            </div>
        )}

        {!isLoading && matchInsights?.status === 'ok' && matchInsights.narrative && (
            <>
                <p className="mb-1.5 mt-1 text-sm leading-[1.55] text-ink-muted whitespace-pre-wrap">
                    Advisory narrative based on recently-rejected URLs. Suggestive — not
                    prescriptive. {matchInsights.narrative}
                </p>
                {matchInsights.themes && matchInsights.themes.length > 0 && (
                    <div className="space-y-0.5">
                        {matchInsights.themes.map((theme, idx) => (
                            <div key={idx} className="py-0.5 text-sm text-ink">
                                <b className="text-accent-active">
                                    {theme.count} URL{theme.count === 1 ? '' : 's'}
                                </b>{' '}
                                rejected for similar reasons: {theme.label}
                                {theme.sampleUrls && theme.sampleUrls.length > 0 && (
                                    <ul className="ml-4 mt-0.5 space-y-0.5">
                                        {theme.sampleUrls.map((url, urlIdx) => (
                                            <li key={urlIdx} className="truncate text-xs text-accent">
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="hover:underline"
                                                    title={url}
                                                >
                                                    {url}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <p className="mt-1.5 text-sm text-ink-muted">
                    Based on {matchInsights.sampleSize ?? 0} unmatched URL{matchInsights.sampleSize === 1 ? '' : 's'} across {matchInsights.runsConsidered ?? 0} recent run{matchInsights.runsConsidered === 1 ? '' : 's'}.
                </p>
            </>
        )}

        {!isLoading && matchInsights?.status === 'no_unmatched' && (
            <p className="py-2 text-sm text-ink">
                No unmatched URLs to analyse yet. Either this blueprint has no scan history, or every recent scan matched. Run a scheduled scan first and try again.
            </p>
        )}

        {!isLoading && (matchInsights?.status === 'llm_error' || matchInsights?.status === 'error') && (
            <p className="py-2 text-sm text-risk">
                Couldn't generate insights: {matchInsights.error || 'unknown error'}. Try again in a moment.
            </p>
        )}
    </div>
);

export default MatchInsightsPanel;
